import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { nameof } from "ts-simple-nameof";

import {
  BookingFilterByCustomers,
  BookingTemplateConstants,
} from "@assets/constants";
import { BookingTemplateTypes } from "@assets/types";
import { ArrayUtilities } from "@assets/utilities";
import BookingTemplateService from "@services/BookingTemplateService";
import type { RootState } from "@state/store";

export const getTemplates = createAsyncThunk<
  BookingTemplateTypes.BookingTemplateData,
  void,
  {
    state: RootState;
  }
>("templates/getTemplates", async (_, { getState }) => {
  const state = getState();
  return await BookingTemplateService.getTemplates(
    state.bookingTemplatesSlice.filters
  );
});

export const getTemplatePartnersFilterValues = createAsyncThunk<
  BookingTemplateTypes.BookingTemplatePartnersFilterValues,
  void,
  { state: RootState }
>("templates/getTemplatePartnersFilterValues", async (_, { getState }) => {
  const state = getState();
  return await BookingTemplateService.getTemplatePartnersFilterValues(
    state.bookingTemplatesSlice.filters.templateCustomer
  );
});

export interface FilterBookingTemplateState {
  searchString: string;
  sortBy: string;
  page: number;
  pageSize: number;
  shipperCode: string;
  consigneeCode: string;
  shippingTypes: string[];
  templateCustomer: BookingFilterByCustomers;
}

export const initialFiltersState: FilterBookingTemplateState = {
  consigneeCode: "",
  page: 1,
  pageSize: 10,
  searchString: "",
  shipperCode: "",
  shippingTypes: [],
  sortBy: BookingTemplateConstants.SORT_BY_VALUES[0].value,
  templateCustomer: BookingFilterByCustomers.CHOSEN_CUSTOMER,
};

export interface BookingTemplatesState {
  templates: BookingTemplateTypes.BookingTemplateHeader[];
  isLoading: boolean;
  totalCount: number;
  filters: FilterBookingTemplateState;
  filterConsignees: BookingTemplateTypes.Partner[];
  filterShippers: BookingTemplateTypes.Partner[];
}

export const initialBookingTemplatesState: BookingTemplatesState = {
  templates: [],
  totalCount: 0,
  isLoading: false,
  filters: initialFiltersState,
  filterConsignees: [],
  filterShippers: [],
};

export const bookingTemplatesSlice = createSlice({
  name: "templates",
  initialState: initialBookingTemplatesState,
  reducers: {
    filterBookingTemplatesShippingType: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.filters.shippingTypes = action.payload;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplatesShipper: (state, action: PayloadAction<string>) => {
      state.filters.shipperCode =
        action.payload || initialFiltersState.shipperCode;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplatesConsignee: (state, action: PayloadAction<string>) => {
      state.filters.consigneeCode =
        action.payload || initialFiltersState.consigneeCode;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplatesReset: (state) => {
      state.filters.shippingTypes = initialFiltersState.shippingTypes;
      state.filters.shipperCode = initialFiltersState.shipperCode;
      state.filters.consigneeCode = initialFiltersState.consigneeCode;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplateSearch: (state, action: PayloadAction<string>) => {
      state.filters.searchString =
        action.payload || initialFiltersState.searchString;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplateSortBy: (state, action: PayloadAction<string>) => {
      state.filters.sortBy = action.payload;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplateWithPagination: (
      state,
      action: PayloadAction<number>
    ) => {
      state.filters.page = action.payload;
    },
    filterBookingTemplateCustomer: (
      state,
      action: PayloadAction<BookingFilterByCustomers>
    ) => {
      state.filters.templateCustomer = action.payload;
      state.filters.page = initialFiltersState.page;
    },
    filterBookingTemplateWithPageSize: (
      state,
      action: PayloadAction<number>
    ) => {
      state.filters.pageSize = action.payload;
      state.filters.page = initialFiltersState.page;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTemplates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        getTemplates.fulfilled,
        (
          state,
          action: PayloadAction<BookingTemplateTypes.BookingTemplateData>
        ) => {
          state.isLoading = false;
          state.templates = action.payload.BookingTemplateHeaders;
          state.totalCount = action.payload.TotalCount;
          state.filters.page = action.payload.PageNumber;
          state.filters.pageSize = action.payload.PageSize;
        }
      )
      .addCase(getTemplates.rejected, (state) => {
        state.isLoading = false;
        state.templates = initialBookingTemplatesState.templates;
        state.totalCount = initialBookingTemplatesState.totalCount;
        state.filters.page = initialBookingTemplatesState.filters.page;
      })
      .addCase(
        getTemplatePartnersFilterValues.fulfilled,
        (
          state,
          action: PayloadAction<BookingTemplateTypes.BookingTemplatePartnersFilterValues>
        ) => {
          state.filterConsignees = action.payload.Consignees;
          state.filterShippers = action.payload.Shippers;
        }
      );
  },
});

export const {
  filterBookingTemplatesShippingType,
  filterBookingTemplatesShipper,
  filterBookingTemplatesConsignee,
  filterBookingTemplatesReset,
  filterBookingTemplateSearch,
  filterBookingTemplateSortBy,
  filterBookingTemplateWithPageSize,
  filterBookingTemplateWithPagination,
  filterBookingTemplateCustomer,
} = bookingTemplatesSlice.actions;

export const bookingTemplatesState = (state: RootState) =>
  state.bookingTemplatesSlice;
export const bookingTemplateFiltersState = (state: RootState) =>
  state.bookingTemplatesSlice.filters;

export const activeBookingTemplateFilters = (state: RootState) =>
  Object.keys(state.bookingTemplatesSlice.filters).filter((key) => {
    // Only take these filters into consideration, as the others are on their own
    if (
      key === nameof<FilterBookingTemplateState>((f) => f.shippingTypes) ||
      key === nameof<FilterBookingTemplateState>((f) => f.shipperCode) ||
      key === nameof<FilterBookingTemplateState>((f) => f.consigneeCode)
    ) {
      const currentFilter = state.bookingTemplatesSlice.filters[key];
      const initialFilter = initialFiltersState[key];
      if (Array.isArray(currentFilter) && Array.isArray(initialFilter)) {
        return !ArrayUtilities.areEqual(currentFilter, initialFilter);
      } else {
        return currentFilter !== initialFilter;
      }
    } else {
      return false;
    }
  });

export default bookingTemplatesSlice.reducer;
